import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo } from "react";

import { HStack, VStack } from "@/components/Layout";
import { ViewportLoader } from "@/components/Loading";
import type { NavigationSectionGroup } from "@/components/Navigation/NavigationCard";
import { defaultNS, useTranslation } from "@/lib/i18n";

import {
  AttributeCard,
  OrganizationNavigation,
  PeopleCard,
  StructureCard,
} from "../-components";
import { useOrganizationStructure } from "../-query";

import { organizationDetailSearchSchema } from "./-zod-schema";

const HASH_IDS = {
  STRUCTURE: "structure",
  PEOPLE: "people",
} as const;

export const Route = createFileRoute("/_staff/organization/$id/_layout/")({
  component: RouteComponent,
  validateSearch: zodValidator(organizationDetailSearchSchema),
});

function RouteComponent() {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.organization.detailPage",
  });
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const baseDate = search.baseDate;

  const { data, isPending } = useOrganizationStructure({
    departmentId: id,
    baseDate,
  });

  const sortedData = data?.sort((a, b) => a.displayOrder - b.displayOrder);
  // セクション情報を動的に生成
  const sections: NavigationSectionGroup[] = useMemo(() => {
    const dynamicSections =
      sortedData?.map((item) => ({
        titleLabel: item.name,
        to: "/organization/$id" as const,
        hash: item.name,
      })) || [];

    const fixedSections = [
      {
        titleLabel: t("structure"),
        to: "/organization/$id" as const,
        hash: HASH_IDS.STRUCTURE,
      },
      {
        titleLabel: t("people"),
        to: "/organization/$id" as const,
        hash: HASH_IDS.PEOPLE,
      },
    ];

    return [...dynamicSections, ...fixedSections];
  }, [sortedData, t]);

  if (isPending) {
    return (
      <VStack className="p-6">
        <ViewportLoader />
      </VStack>
    );
  }

  return (
    <div className="p-6 gap-6">
      <HStack className="gap-6 items-start">
        <div className="sticky top-6 max-w-60">
          <OrganizationNavigation sections={sections} />
        </div>
        <VStack className="gap-6 w-full">
          {sortedData?.map((item) => {
            return (
              <div key={item.name} id={item.name}>
                <AttributeCard
                  name={item.name}
                  description={item.description}
                  attributes={item.attributes}
                />
              </div>
            );
          })}

          <div id={HASH_IDS.STRUCTURE}>
            <StructureCard />
          </div>

          <div id={HASH_IDS.PEOPLE}>
            <PeopleCard />
          </div>
        </VStack>
      </HStack>
    </div>
  );
}
